import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
// The locales the app supports besides English, which moment ships by default.
// index.js registers them too; importing here keeps dates localised wherever
// the component is mounted, the way page-dashboard-v2 does it.
import 'moment/locale/sv';
import 'moment/locale/nb';
import 'moment/locale/da';
import SearchInput from '../../components/shared/SearchInput';
import styles from './page-updates.module.scss';
import { Newsletter } from '../../services/newsletterService';

type Props = {
  /** Already filtered by the search query. */
  newsletters: Newsletter[],
  selectedId?: string,
  onSelect: (id: string) => void,
  query: string,
  onQueryChange: (query: string) => void,
  visibleCount: number,
  onShowMore: () => void,
  /** Everything in this tab, so the placeholder can name the real number. */
  total: number,
}

/** Follows the interface language rather than pinning every user to Swedish. */
export const formatSentAt = (sentAt: string, language: string) =>
  moment(sentAt).locale(language).format('D MMMM YYYY');

const NewsletterRail: FC<Props> = ({
  newsletters, selectedId, onSelect, query, onQueryChange, visibleCount, onShowMore, total,
}) => {
  const [t, i18n] = useTranslation('updates');
  const remaining = newsletters.length - visibleCount;

  return (
    <div className={styles.rail}>
      <SearchInput
        className={styles.railSearch}
        value={query}
        placeholder={t('search.placeholder', {
          count: total,
          defaultValue: 'Sök bland {{count}} nyhetsbrev…',
        })}
        onChange={(e: any) => onQueryChange(e.target.value)}
      />

      {newsletters.length === 0 && (
        <p className={styles.railEmpty}>
          {t('search.noResults', 'Inga nyhetsbrev matchar din sökning.')}
        </p>
      )}

      <ul className={styles.railList}>
        {newsletters.slice(0, visibleCount).map(newsletter => (
          <li key={newsletter.id}>
            <button
              type="button"
              className={newsletter.id === selectedId
                ? `${styles.railRow} ${styles.selected}`
                : styles.railRow}
              aria-current={newsletter.id === selectedId ? 'true' : undefined}
              onClick={() => onSelect(newsletter.id)}
            >
              <span className={styles.railEdge}></span>
              <span className={styles.railRowBody}>
                <span className={styles.railMeta}>
                  {formatSentAt(newsletter.sentAt, i18n.language)}
                </span>
                {/* The subject lines are interchangeable ("Produktuppdateringar
                    V.33"), so the preview text is the headline where there is
                    one. */}
                <span className={styles.railTitle}>
                  {newsletter.summary ?? newsletter.title}
                </span>
              </span>
            </button>
          </li>
        ))}
      </ul>

      {remaining > 0 && (
        <button type="button" className={styles.railMore} onClick={onShowMore}>
          {t('showMore', { count: remaining, defaultValue: 'Visa fler ({{count}} kvar)' })}
        </button>
      )}
    </div>
  );
};

export default NewsletterRail;
